import { get, writable } from "svelte/store";
import { pb } from "$lib/pocketbase";
import { goto } from "$app/navigation";
import type { JobConfig } from "$lib/types";
import { persisted } from "svelte-persisted-store";
import { parse as parseYaml, stringify as stringifyYaml } from "yaml";
import * as cluster from "node:cluster";
import { _ } from "$lib/i18n";
let apiUrl = "";
export const areas = persisted("areas", []);
try {
  const protocol = window.location.protocol === "https:" ? "https" : "http";

  if (import.meta.env.MODE === "development") {
    apiUrl = "http://localhost:8080"; // Dev-Modus
  } else {
    apiUrl = `${protocol}://${window.location.host}`; // Produktionsmodus
  }
} catch (error) {
  // Fallback für den Server-Umfeld
  apiUrl = "http://localhost:8080";
}
export { apiUrl };
export async function getConfigs() {
  return await pb.collection("configs").getFullList({
    sort: "-created"
  });
}

export async function deleteConfig(configToDelete: any) {
  try {
    if (!configToDelete || !configToDelete.id) {
      throw new Error(get(_)("app.api.errors.invalidConfig"));
    }
    await pb.collection("configs").delete(configToDelete.id);
  } catch (error: any) {
    alert(error.message);
  }
}

export async function uploadConfigFile(configFile: File) {
  if (!configFile || !configFile.name) {
    alert(get(_)("app.api.errors.selectConfigFile"));
    return false;
  }

  const fileName = configFile.name;
  const fileExtension = fileName.split(".").pop()?.toLowerCase();

  if (!fileExtension || !["json", "yaml", "yml"].includes(fileExtension)) {
    alert(get(_)("app.api.errors.invalidFileFormat"));
    return false;
  }

  const baseName = fileName.replace(/\.[^/.]+$/, "");
  const targetFilename = `${baseName}.json`;

  try {
    const isUnique = await isConfigFilenameUnique(targetFilename);
    if (!isUnique) {
      alert(
        get(_)("app.api.errors.configNameExists", {
          values: { filename: targetFilename }
        })
      );
      return false;
    }
  } catch (error) {
    console.error("Fehler bei der Überprüfung des Dateinamens:", error);
    alert(get(_)("app.api.errors.filenameCheckError"));
    return false;
  }

  try {
    const fileContent = await configFile.text();
    let parsedData;

    if (fileExtension === "json") {
      parsedData = JSON.parse(fileContent);
    } else if (fileExtension === "yaml" || fileExtension === "yml") {
      parsedData = parseYaml(fileContent);
    } else {
      alert(get(_)("app.api.errors.invalidFormatSupported"));
      return false;
    }
    const jsonString = JSON.stringify(parsedData, null, 2); // Schön formatierte JSON-Daten

    const convertedFile = new File([jsonString], targetFilename, {
      type: "application/json"
    });
    const formData = new FormData();
    formData.append("filename", convertedFile.name); // Name der konvertierten Datei
    formData.append("file", convertedFile); // Datei hinzufügen

    await pb.collection("configs").create(formData);
    return true;
  } catch (error: any) {
    console.error("Fehler beim Verarbeiten der Datei:", error);

    if (error instanceof SyntaxError) {
      alert(get(_)("app.api.errors.invalidJsonYaml"));
      return false;
    } else if (error.status === 400) {
      alert(get(_)("app.api.errors.uploadErrorInvalidRequest"));
      return false;
    } else {
      alert(get(_)("app.api.errors.fileProcessingError"));
      return false;
    }
  }
  return true;
}
export async function uploadPDFFile(pdfFile: any) {
  if (!pdfFile || !pdfFile.name) {
    alert(get(_)("app.api.errors.selectPdfFile"));
    return;
  }

  if (!pdfFile.name.endsWith(".pdf")) {
    alert(get(_)("app.api.errors.uploadPdfOnly"));
    return;
  }

  const formData = new FormData();
  formData.append("filename", pdfFile.name);
  formData.append("file", pdfFile);

  // Füge hier das "return" hinzu
  return await pb.collection("pdfs").create(formData);
}
export async function downloadConfig(selectedConfig: any) {
  const url = pb.files.getUrl(selectedConfig, selectedConfig.file, {});
  window.open(url + "?download=1", "_blank"); // Externe Weiterleitung in einem neuen Tab
}

export async function downloadConfigAsJson(selectedConfig: any) {
  const configData = await downloadAndParseConfig(selectedConfig);
  if (configData) {
    const jsonString = JSON.stringify(configData, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = selectedConfig.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export async function downloadConfigAsYaml(selectedConfig: any) {
  const configData = await downloadAndParseConfig(selectedConfig);
  if (configData) {
    try {
      const yamlString = stringifyYaml(configData);
      const blob = new Blob([yamlString], { type: "application/yaml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = withoutJsonEnding(selectedConfig.filename) + ".yaml";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Error converting to YAML or downloading:", e);
      alert("Fehler beim Konvertieren zu YAML.");
    }
  }
}

export async function downloadAndParseConfig(
  selectedConfig: any
): Promise<JobConfig | null> {
  const url = pb.files.getUrl(selectedConfig, selectedConfig.file, {});
  const cacheBustedUrl = new URL(url);
  cacheBustedUrl.searchParams.set("cb", Date.now().toString());

  try {
    const response = await fetch(cacheBustedUrl.toString(), {
      method: "GET",
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache"
      }
    });

    if (!response.ok) {
      throw new Error(
        get(_)("app.api.errors.httpError", {
          values: { status: response.status }
        })
      );
    }

    const text = await response.text();
    const jsonData = JSON.parse(text);

    // Verwende Type Assertion, um TypeScript zu sagen, dass jsonData ein JobConfig ist
    const jobConfig: JobConfig = jsonData as JobConfig;

    return jobConfig;
  } catch (error) {
    console.error(get(_)("app.api.errors.downloadParseError"), error);
    return null;
  }
}

export async function fetchAreas() {
  try {
    // Alle Records aus der Collection 'areas' abrufen
    const records = await pb.collection("areas").getFullList({
      sort: "-created" // Sortierung nach Erstellungsdatum absteigend
    });

    // Die Daten in das gewünschte Format umwandeln
    const areas = records.map((area) => ({
      x: area.x,
      y: area.y,
      x2: area.x2,
      y2: area.y2,
      value: `${area.x}, ${area.y}, ${area.x2}, ${area.y2}`,
      showDetails: false,
      draw: true,
      name: area.name,
      id: area.id
    }));

    // Optional: Vorherigen Zeichnungsstatus abrufen und anwenden
    for (const area of areas) {
      const previousDrawState = getPreviousDrawState(area.id); // Funktion, um vorherigen Bereichszeichnungsstatus basierend auf der ID zu erhalten
      if (previousDrawState !== null) {
        area.draw = previousDrawState;
      }
    }

    return areas;
  } catch (error) {
    console.error("Es gab ein Problem mit der Fetch-Operation:", error);
    return { error: true };
  }
}

export async function getPdfs() {
  try {
    // Alle Records aus der Collection 'pdfs' abrufen
    const pdfs = await pb.collection("pdfs").getFullList({
      sort: "-created"
    });

    return pdfs;
  } catch (error) {
    console.error("Es gab ein Problem mit der Fetch-Operation:", error);
    return { error: true };
  }
}

export async function deletePdf(pdfToDelete: any) {
  try {
    if (!pdfToDelete || !pdfToDelete.id) {
      throw new Error(get(_)("app.api.errors.invalidPdf"));
    }
    await pb.collection("pdfs").delete(pdfToDelete.id);
  } catch (error: any) {
    alert(error.message);
  }
}

export async function downloadPdf(selectedPdf: any) {
  if (!selectedPdf || !selectedPdf.file) {
    return;
  }
  const url = pb.files.getUrl(selectedPdf, selectedPdf.file, {});

  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = selectedPdf.filename || "download.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Fehler beim Herunterladen des PDFs:", error);
  }
}

function getPreviousDrawState(areaId: any) {
  if (Array.isArray(get(areas))) {
    // @ts-ignore
    const area = get(areas).find((area) => area.id === areaId);
    // @ts-ignore
    return area ? area.draw : null;
  } else {
    return null;
  }
}

export function downloadBlobFromBase64(
  base64Content: string,
  filename: string,
  type: string
) {
  const byteCharacters = atob(base64Content);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: type });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";

  const clickHandler = () => {
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.removeEventListener("click", clickHandler);
    }, 150);
  };

  a.addEventListener("click", clickHandler, false);
  a.click();

  return a;
}

export async function getConfigByFilename(filename: string) {
  // Abrufen des ersten Config-Records mit dem angegebenen Dateinamen
  const record = await pb
    .collection("configs")
    .getFirstListItem(`filename="${filename}"`);
  return record;
}

export async function isConfigFilenameUnique(filename: string) {
  try {
    const item = await pb
      .collection("configs")
      .getFirstListItem(`filename="${filename}"`);
    return !item;
  } catch (error: any) {
    if (error.status === 404) {
      return true;
    }
    console.error("Fehler bei der Überprüfung des Dateinamens:", error);
    throw error;
  }
}
export function createBlobBase64(base64Content: string) {
  try {
    const base64 = base64Content.replace(/^data:application\/pdf;base64,/, "");

    const chunkSize = 8192;
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += chunkSize) {
      const slice = byteCharacters.slice(offset, offset + chunkSize);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: "application/pdf" });
    return blob;
  } catch (error) {
    console.error("Fehler beim Erstellen des Blobs aus Base64:", error);
    throw error;
  }
}

export function withoutJsonEnding(filename: string) {
  return filename.replace(/\.json$/, "");
}
